@import "me-design/tokens";

/* Kundeninfo-eigene Tokens und Grundstile — NACH den ME-Tokens eingebunden.

   Die einzige Farbquelle ist die Tokendatei von me-design (oben eingebunden): hell und dunkel
   schaltet me-design selbst (data-theme="hell|dunkel" am <html>, gesetzt von applyTheme, bzw.
   prefers-color-scheme ohne gewähltes Schema). Die Unternehmensfarbe setzt data-unternehmen
   (me|bs|pa), die Schriftstufe data-schrift. Seit ME-Etappe 4 (P11) gibt es keine ki-Alias-Tokens
   mehr; scripts/check-tokens.mjs bricht bei jeder Stelle, die eines schreibt oder liest. Was hier
   steht, sind Kundeninfo-eigene Größen ohne Entsprechung in me-design (Zeilenabstand, Tap-Ziel,
   Kopfhöhe, Auflagen der Kopfleiste) und Farbmischungen aus ME-Tokens.

   Die tatsächlichen Farbwerte stehen gespiegelt in tokens.ts (PALETTE); test/contrast.test.ts
   hält sie gegen die ME-Datei deckungsgleich. */

:root {
  /* Die Hinterlegungen der Statusfarben: 12 % des Tons auf der Fläche. Kundeninfo-eigen,
     aber ohne Alias-Präfix: me-design liefert dafür kein Token. Aus ME-Tokens gemischt. */
  --ok-grund:       color-mix(in srgb, var(--ok) 12%, var(--flaeche));
  --warnung-grund:  color-mix(in srgb, var(--warnung) 12%, var(--flaeche));
  --fehler-grund:   color-mix(in srgb, var(--fehler) 12%, var(--flaeche));
  --laeuft-grund:   color-mix(in srgb, var(--status-laeuft) 12%, var(--flaeche));
  --aus-grund:      color-mix(in srgb, var(--status-aus) 12%, var(--flaeche));

  /* Grundschrift: die Schriftstufe (data-schrift) setzt --schrift-basis 15/17/19/22 px.
     Dichte und Zeigergerät verschieben nur Abstände und Tap-Ziele. */
  --zeilenabstand: 6px;
  --tap-min: 28px;

  /* Ohne Wahl (SYSTEM, kein data-theme) folgen native Felder und Rollbalken dem System;
     mit Wahl dem gewählten Schema (Regeln unter diesem Block, Gesamtreview Etappe 4, H9). */
  color-scheme: light dark;

  /* ------------------------------------------------------------------------------
   * Umgebungskennzeichnung (Spec "Umgebungskennzeichnung", 07.09.2026).
   *
   * Die sechs Grundwerte --umgebung-produktion/-test/-entwicklung/-schrift/-sekundaer/
   * -fokus kommen seit ME-Etappe 1 (Aufgabe 4) aus me-design/tokens (gleiche Namen, gleiche
   * Werte). Hier stehen nur noch die Kundeninfo-eigenen Ergänzungen; auch sie tragen
   * die Namen der Spec und deshalb kein ki-Präfix.
   *
   * Sie stehen NUR in diesem Block und in KEINEM der beiden Dunkel-Blöcke unten, und
   * genau das ist die Zusage: Die Kopfleistenfarbe ist THEME-UNABHÄNGIG. Wäre sie je
   * Schema verschieden, sagte dasselbe System je nach Geräteeinstellung etwas anderes
   * über sich — und ein Signal, das man erst deuten muss, ist keins. Die Spec verlangt
   * dafür in ihrem eigenen Design-System dieselben Werte in allen drei Theme-Blöcken;
   * hier ist die Kaskade der kürzere Weg zum selben Ergebnis: Was in den Dunkel-Blöcken
   * nicht steht, kann dort auch nicht auseinanderlaufen. Der Test in
   * test/umgebung.test.ts hält beides fest.
   *
   * Die dunkle Schrift der hellen Testwelt (Schrift, Sekundärton, Fokus) setzt seit
   * ME-Etappe 1 (Aufgabe 6) das Element me-kopfzeile selbst; die früheren
   * --umgebung-*-test-Schrifttokens sind entfallen.
   * ------------------------------------------------------------------------------ */

  /* Die drei Auflagen der Kopfleiste — die Pille unter dem aktiven Bereich, der Schweif
   * unter einem berührten Bereich und die Fläche eines aufgeklappten Werkzeug-Knopfes.
   * Sie sind AUFHELLUNGEN des jeweiligen Untergrunds und deshalb je Welt verschieden:
   * Auf den beiden dunklen Welten ein Weissanteil, auf der hellen Testwelt (Bernstein)
   * ein Schwarzanteil — Weiss auf Bernstein wäre keine Hervorhebung, sondern ein Fleck.
   * Werte aus dem Kopfleisten-Schaufenster des Design-Systems. */
  --umgebung-pille:            rgba(255, 255, 255, 0.18);
  --umgebung-schweb:           rgba(255, 255, 255, 0.10);
  --umgebung-werkzeug-schweb:  rgba(255, 255, 255, 0.16);
  --umgebung-pille-test:           rgba(0, 0, 0, 0.14);
  --umgebung-schweb-test:          rgba(0, 0, 0, 0.08);
  --umgebung-werkzeug-schweb-test: rgba(0, 0, 0, 0.08);

  /* Die 3px-Akzentlinie zeichnet seit ME-Etappe 1 (Aufgabe 6) me-kopfzeile selbst, in
   * `--marke` des Arbeitsunternehmens; --umgebung-akzent(-test) sind entfallen. */

  /* Die feste Höhe der Hauptzeile. FEST, nicht Mindesthöhe: Inhalt wird darin zentriert,
   * Schrift- und Logogrössen richten sich nach ihr und nicht umgekehrt. Die Akzentlinie
   * liegt AUSSERHALB dieser 52px. */
  --kopfhoehe: 52px;
}

/* Das gewählte Schema legt auch das color-scheme fest — sonst zeichnete der Browser die
   Rollbalken (und bis P17 die nativen Kästchen und Radios, seither me-optionen mit eigenem
   `--farbschema`) bei „Hell“ auf dunklem System dunkel auf hellem Grund (Gesamtreview
   Etappe 4, H9). */
:root[data-theme="hell"] { color-scheme: light; }
:root[data-theme="dunkel"] { color-scheme: dark; }

/* Größenklassen: nur noch Tap-Ziele und Zeilenabstand. Die Grundschrift staffelt sich
   nicht mehr nach Breite — sie ist die Schriftstufe (data-schrift, Benutzereinstellung
   mit Gerätevorgabe, siehe schriftAus in theme/applyTheme.ts). */
@media (max-width: 743px)  { :root { --tap-min: 44px; --zeilenabstand: 10px; } }
@media (min-width: 744px) and (max-width: 1279px) { :root { --tap-min: 44px; --zeilenabstand: 8px; } }
@media (min-width: 1280px) { :root { --tap-min: 28px; --zeilenabstand: 6px; } }

/* Zweite Achse: das Zeigergerät, nicht die Fensterbreite. Der Entwurf nennt 44 pt als
   Eigenschaft von TOUCH-Geräten — ein iPad Pro im Querformat misst aber 1366 px und fiele
   damit in die Desktop-Staffel: 28-px-Ziele auf einem reinen Touch-Gerät. "pointer:
   coarse" beschreibt genau das (Finger statt Maus) und steht bewusst NACH den
   Breitenregeln, damit es bei gleicher Spezifität gewinnt. Ein Desktop-Fenster mit Maus
   bleibt unberührt. */
@media (pointer: coarse) {
  :root { --tap-min: 44px; --zeilenabstand: 8px; }
}

/* Dichte: ein reiner Kundeninfo-Schalter (Benutzereinstellung "Dichte" → Zeilenabstand), den
   me-design nicht kennt — seine Schriftstufe data-schrift ist eine andere Achse. Bleibt deshalb
   als data-ki-density (P11, bewusst). */
:root[data-ki-density="comfortable"] { --zeilenabstand: 12px; }

body {
  margin: 0;
  background: var(--grund);
  color: var(--text);
  font-family: var(--schrift);
  font-size: var(--schrift-basis);
  line-height: 1.45;
  -webkit-font-smoothing: antialiased;
}

/* Beträge und Nummern immer mit gleich breiten Ziffern. */
.ki-num { font-variant-numeric: tabular-nums; text-align: right; }
